import React, { useState } from 'react';
import Field, { CheckField } from '../Field';
import AutoTextarea from '../AutoTextarea';
import ItemListEditor from './ItemListEditor';
import {
  appendItem,
  buildToPlot,
  padCells,
  parseCsvTable,
  removeItem,
  replaceItem,
  rowCells,
  wrapRows,
} from './helpers';

/**
 * dataset: a table of figures, optionally charted.
 *
 * Firestore cannot nest an array inside an array, so each row is stored wrapped
 * as {"cells": [...]}. This editor works in those wrapped rows directly, so any
 * extra key an import left on a row survives an edit.
 */
export default function DatasetEditor({ idPrefix, data, onChange }) {
  const d = data || {};
  const set = (patch) => onChange({ ...d, ...patch });
  const [csv, setCsv] = useState('');
  const [csvHasHeader, setCsvHasHeader] = useState(true);
  const [csvError, setCsvError] = useState(null);

  const columns = Array.isArray(d.column_headers) ? d.column_headers : [];
  const width = Math.max(columns.length, 1);
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((row) => ({
    ...(row && !Array.isArray(row) && typeof row === 'object' ? row : {}),
    cells: padCells(rowCells(row), width),
  }));

  const setColumns = (next) => set({ column_headers: next });

  const onParseCsv = () => {
    const parsed = parseCsvTable(csv, { hasHeader: csvHasHeader, columns });
    if (!parsed.rows.length) {
      setCsvError('Nothing to parse — paste rows of comma-, tab- or semicolon-separated values.');
      return;
    }
    setCsvError(null);
    set({ column_headers: parsed.columns, rows: wrapRows(parsed.rows) });
    setCsv('');
  };

  const onRebuildChart = () => set({ to_plot: buildToPlot(columns, rows.map((r) => r.cells)) });

  return (
    <>
      <Field id={`${idPrefix}-title`} label="Title" hint="The heading above the figures.">
        <input id={`${idPrefix}-title`} type="text" className="input" value={d.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Visitors by year" />
      </Field>

      <Field id={`${idPrefix}-description`} label="Caption" hint="Markdown, shown directly above the figures.">
        <AutoTextarea id={`${idPrefix}-description`} value={d.description || ''} onChange={(e) => set({ description: e.target.value })} placeholder="Recreation visits, by calendar year." minRows={2} />
      </Field>

      {/* Columns as chips: each header is editable in place. */}
      <div className="field">
        <div className="card-header" style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Columns <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>({columns.length})</span>
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setColumns(appendItem(columns, ''))}>+ Add column</button>
        </div>
        {columns.length === 0 ? (
          <p className="hint" style={{ margin: '4px 0 0' }}>No columns yet. Add them here, or paste a CSV below with its header row.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {columns.map((col, i) => (
              /* eslint-disable-next-line react/no-array-index-key */
              <span key={`${idPrefix}-col-${i}`} className="chip" style={{ gap: 4, paddingRight: 2 }}>
                <label htmlFor={`${idPrefix}-col-${i}`} className="sr-only">{`Column ${i + 1} heading`}</label>
                <input
                  id={`${idPrefix}-col-${i}`}
                  type="text"
                  value={col === null || col === undefined ? '' : col}
                  onChange={(e) => setColumns(replaceItem(columns, i, e.target.value))}
                  placeholder={`Column ${i + 1}`}
                  size={Math.max(6, String(col || '').length)}
                  style={{ border: 0, background: 'transparent', color: 'inherit', font: 'inherit', padding: '0 2px', minWidth: 48 }}
                />
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setColumns(removeItem(columns, i))} aria-label={`Remove column ${i + 1}`} title="Remove column" style={{ padding: '0 4px', lineHeight: 1 }}>✕</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <ItemListEditor
        idPrefix={`${idPrefix}-row`}
        label="Rows"
        items={rows}
        onChange={(next) => set({ rows: next })}
        makeItem={() => ({ cells: padCells([], width) })}
        addLabel="+ Add row"
        itemLabel={(i) => `Row ${i + 1}`}
        emptyText="No rows yet. Add one, or paste a CSV below."
        renderItem={({ item, id, update }) => (
          <div className="field-row" style={{ marginBottom: 0 }}>
            {padCells(item.cells, width).map((cell, c) => (
              /* eslint-disable-next-line react/no-array-index-key */
              <Field key={`${id}-c${c}`} id={`${id}-c${c}`} label={columns[c] || `Column ${c + 1}`}>
                <input
                  id={`${id}-c${c}`}
                  type="text"
                  className="input"
                  value={cell === null || cell === undefined ? '' : cell}
                  onChange={(e) => update({ cells: replaceItem(padCells(item.cells, width), c, e.target.value) })}
                />
              </Field>
            ))}
          </div>
        )}
      />

      <details style={{ marginBottom: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--color-muted)' }}>Paste a CSV</summary>
        <div style={{ marginTop: 10 }}>
          <Field id={`${idPrefix}-csv`} label="Comma-, tab- or semicolon-separated values" error={csvError} hint="Quoted fields are supported. Parsing replaces the columns and rows above.">
            <textarea
              id={`${idPrefix}-csv`}
              className={`textarea${csvError ? ' is-invalid' : ''}`}
              value={csv}
              onChange={(e) => { setCsv(e.target.value); setCsvError(null); }}
              rows={5}
              spellCheck={false}
              placeholder={'Year,Visitors\n2021,"5,039,835"\n2022,"4,692,417"'}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
          </Field>
          <CheckField id={`${idPrefix}-csv-header`} label="First row holds the column headings" checked={csvHasHeader} onChange={setCsvHasHeader} />
          <button type="button" className="btn btn-secondary btn-sm" onClick={onParseCsv} disabled={!csv.trim()}>Parse into rows</button>
        </div>
      </details>

      <div className="field-row">
        <Field id={`${idPrefix}-footnote`} label="Source" hint="Attribution shown under the table.">
          <input id={`${idPrefix}-footnote`} type="text" className="input" value={d.footnote || ''} onChange={(e) => set({ footnote: e.target.value })} placeholder="Source: National Park Service" />
        </Field>
        <Field id={`${idPrefix}-updated_at`} label="Figures updated" hint="Shown as a date; YYYY-MM-DD.">
          <input id={`${idPrefix}-updated_at`} type="text" className="input" value={d.updated_at || ''} onChange={(e) => set({ updated_at: e.target.value })} placeholder="2026-09-01" />
        </Field>
      </div>

      <CheckField id={`${idPrefix}-show_table`} label="Show the table" checked={d.show_table !== false} onChange={(v) => set({ show_table: v })} />
      <CheckField id={`${idPrefix}-show_graph`} label="Show the chart" checked={d.show_graph !== false} onChange={(v) => set({ show_graph: v })} />

      <details style={{ marginBottom: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--color-muted)' }}>
          Chart data and prose ({(Array.isArray(d.to_plot) ? d.to_plot.length : 0)} plotted rows)
        </summary>
        <div style={{ marginTop: 10 }}>
          <Field id={`${idPrefix}-body`} label="Surrounding prose" hint="Markdown, rendered between the heading and the figures.">
            <AutoTextarea id={`${idPrefix}-body`} value={d.body || ''} onChange={(e) => set({ body: e.target.value })} minRows={3} />
          </Field>
          <p className="hint" style={{ marginTop: 0 }}>
            The chart reads <code>to_plot</code>: the same figures with a header row first and the
            separators stripped, because it cannot parse “5,039,835”.
          </p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRebuildChart} disabled={!rows.length}>
            Rebuild chart data from the table
          </button>
          <Field id={`${idPrefix}-jsonld`} label="schema.org Dataset (JSON-LD)" hint="Stored as text and emitted verbatim; leave alone unless you know the shape." style={{ marginTop: 12 }}>
            <AutoTextarea id={`${idPrefix}-jsonld`} value={d.jsonld || ''} onChange={(e) => set({ jsonld: e.target.value })} minRows={3} spellCheck={false} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </Field>
        </div>
      </details>
    </>
  );
}
